import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { keywordGroupsTransforms } from "app/services/keyword-groups";
import type { IIntentDistributionChartData } from "app/pages/keyword-research/common-components/intent-distribution-chart/intentDistributionChartTypes";
import type { Filter } from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";

import { useIntentData } from "../hooks/useIntentData";

import { IntentWrapper } from "./intentStyledComponents";
import { KeywordGroupIntent } from "./KeywordGroupIntent";
import { KeywordIntent } from "./KeywordIntent";

const WIDGET_TITLE_KEY = "keyword.analysis.overview.page.v2.intent.title";
const WIDGET_TOOLTIP_KEY = "keyword.analysis.overview.page.v2.intent.tooltip";

interface IIntent {
    subtitleFilters: Filter[];
}

export const Intent: FC<IIntent> = ({ subtitleFilters = [] }) => {
    const { keyword } = useCurrentStateAndParams().params;
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keyword);

    const { intentData, isLoading, error } = useIntentData(isKeywordGroup);

    const noDataText = error
        ? {
              title: translate("common.error"),
              subtitle: translate("common.error.subtitle"),
          }
        : {
              title: translate("common.noresults"),
              subtitle: translate("common.noresults.subtitle"),
          };

    return (
        <IntentWrapper data-automation="Intent">
            <WidgetContainer
                title={translate(WIDGET_TITLE_KEY)}
                tooltip={translate(WIDGET_TOOLTIP_KEY)}
                subtitleFilters={subtitleFilters}
                isLoading={false}
                withBorder={true}
                noData={!isLoading ? !intentData : false}
                noDataTitle={noDataText.title}
                noDataSubtitle={noDataText.subtitle}
                height={"330px"}
            >
                {isKeywordGroup ? (
                    <KeywordGroupIntent
                        data={intentData as IIntentDistributionChartData}
                        isLoading={isLoading}
                    />
                ) : (
                    <KeywordIntent
                        keyword={keyword}
                        intent={intentData as string}
                        isLoading={isLoading}
                    />
                )}
            </WidgetContainer>
        </IntentWrapper>
    );
};
